import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';

import './LongevityChecklist.css';
import images from '../images/longevity';
import siteImages from '../images/site';
import AppStoreBadgeSvg from '../AppStoreBadgeSvg';
import qrCode from '../images/LongevityQrCode.png';
import {
  APP_STORE_ID,
  APP_STORE_URL,
  PAGE_PATH,
  PRIVACY_PATH,
  SITE_URL,
  SUPPORT_EMAIL,
  content,
} from './content.mjs';

export { images };

const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=optional';

/**
 * Per-page <head> management for a route inside the shared CRA shell.
 * The static HTML written by scripts/longevity-postbuild.js already carries
 * these tags for crawlers; this hook keeps them right during client-side
 * navigation and restores the shell's defaults on unmount.
 */
export function usePageChrome({ title, description, path }) {
  useEffect(() => {
    const html = document.documentElement;
    const head = document.head;
    html.classList.add('lc-page-active');

    const previousTitle = document.title;
    document.title = title;

    const added = [];
    const restore = [];

    const ensure = (selector, create) => {
      let el = head.querySelector(selector);
      if (!el) {
        el = create();
        head.appendChild(el);
        added.push(el);
      }
      return el;
    };
    const setAttr = (el, name, value) => {
      restore.push([el, name, el.getAttribute(name)]);
      el.setAttribute(name, value);
    };

    const desc = ensure('meta[name="description"]', () => {
      const m = document.createElement('meta');
      m.name = 'description';
      return m;
    });
    setAttr(desc, 'content', description);

    const canonical = ensure('link[rel="canonical"]', () => {
      const l = document.createElement('link');
      l.rel = 'canonical';
      return l;
    });
    setAttr(canonical, 'href', `${SITE_URL}${path}`);

    ensure('meta[name="apple-itunes-app"]', () => {
      const m = document.createElement('meta');
      m.name = 'apple-itunes-app';
      m.content = `app-id=${APP_STORE_ID}`;
      return m;
    });

    ensure(`link[href="${FONT_HREF}"]`, () => {
      const l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = FONT_HREF;
      return l;
    });

    if (!window.location.hash) {
      // Instant: the class above turns on smooth scrolling, which would
      // otherwise animate a page reached mid-scroll up to the top.
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }

    return () => {
      html.classList.remove('lc-page-active');
      document.title = previousTitle;
      restore.forEach(([el, name, value]) => {
        if (value === null) el.removeAttribute(name);
        else el.setAttribute(name, value);
      });
      added.forEach((el) => el.remove());
    };
  }, [title, description, path]);
}

/**
 * Fades each `.lc-reveal` inside `ref` in as it scrolls into view.
 *
 * Only blocks that start below the fold are hidden (`is-pending`), and that
 * happens before the first paint. Anything already on screen when the page
 * mounts is left alone: fading it in would read as pop-in on a tall window.
 */
export function useReveal(ref) {
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) return undefined;
    const targets = Array.from(root.querySelectorAll('.lc-reveal')).filter(
      (el) => el.getBoundingClientRect().top >= window.innerHeight,
    );
    targets.forEach((el) => el.classList.add('is-pending'));
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('is-pending');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
    );
    targets.forEach((el) => io.observe(el));
    return () => {
      io.disconnect();
      targets.forEach((el) => el.classList.remove('is-pending'));
    };
  }, [ref]);
}

export function SkipLink() {
  return (
    <a className="lc-skip" href="#lc-main">
      Skip to content
    </a>
  );
}

/** True on a mouse-and-keyboard device, where a QR code beats an App Store link. */
function isDesktop() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  const finePointer = window.matchMedia(
    '(hover: hover) and (pointer: fine)',
  ).matches;
  const appleMobile = /iPhone|iPad|iPod/.test(navigator.userAgent);
  return finePointer && !appleMobile;
}

/**
 * Native <dialog> holding the App Store QR code. Opened from the nav button on
 * desktop; Escape, the close button and a backdrop click all dismiss it.
 */
function QrDialog({ open, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return undefined;
    if (open && !dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    } else if (!open && dialog.open) {
      dialog.close();
    }
    const handleClose = () => onClose();
    dialog.addEventListener('close', handleClose);
    return () => dialog.removeEventListener('close', handleClose);
  }, [open, onClose]);

  const onBackdropClick = (event) => {
    if (event.target === ref.current) onClose();
  };

  return (
    <dialog
      className="lc-dialog"
      ref={ref}
      onClick={onBackdropClick}
      aria-labelledby="lc-qr-title"
    >
      <div className="lc-dialog-card">
        <button
          type="button"
          className="lc-dialog-close"
          onClick={onClose}
          aria-label="Close"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <path
              d="M1 1l12 12M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </button>
        <span className="lc-label">{content.qr.label}</span>
        <h2 id="lc-qr-title">{content.qr.heading}</h2>
        {open && (
          <img
            className="lc-qr"
            src={qrCode}
            alt={content.qr.alt}
            width="390"
            height="390"
          />
        )}
        <p className="lc-muted">{content.qr.body}</p>
        <a className="lc-inline-link" href={APP_STORE_URL}>
          {content.qr.link}
        </a>
      </div>
    </dialog>
  );
}

const QrContext = createContext(() => {});

/**
 * Owns the one QR dialog for a page. Every App Store link inside it (nav
 * button and badges) opens the dialog on desktop and links out elsewhere.
 */
export function QrProvider({ children }) {
  const [qrOpen, setQrOpen] = useState(false);
  const closeQr = useCallback(() => setQrOpen(false), []);
  const openQr = useCallback(() => setQrOpen(true), []);
  return (
    <QrContext.Provider value={openQr}>
      {children}
      <QrDialog open={qrOpen} onClose={closeQr} />
    </QrContext.Provider>
  );
}

/** Click handler for App Store links: a real link for keyboard users and no-JS; desktop pointers get the QR code. */
function useStoreClick() {
  const openQr = useContext(QrContext);
  return useCallback(
    (event) => {
      if (!isDesktop()) return;
      event.preventDefault();
      openQr();
    },
    [openQr],
  );
}

export function Nav({ home = false }) {
  const [scrolled, setScrolled] = useState(false);
  const onCtaClick = useStoreClick();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Inlined as data URIs by scripts/site-images.py, so no request.
  const icon = siteImages['longevity-icon'];
  const wordmark = (
    <>
      <img
        src={icon.src}
        srcSet={icon.srcSet}
        sizes="28px"
        alt=""
        width="28"
        height="28"
      />
      <span>
        Longevity <span className="lc-wordmark-2">Checklist</span>
      </span>
    </>
  );

  return (
    <header className={`lc-nav${scrolled ? ' is-scrolled' : ''}`}>
      <div className="lc-container lc-nav-inner">
        {home ? (
          <a
            className="lc-wordmark"
            href="#lc-top"
            aria-label="Longevity Checklist, back to top"
          >
            {wordmark}
          </a>
        ) : (
          <Link
            className="lc-wordmark"
            to={PAGE_PATH}
            aria-label="Longevity Checklist home"
          >
            {wordmark}
          </Link>
        )}
        {home && (
          <nav aria-label="Page sections">
            <ul className="lc-nav-links">
              {content.nav.links.map((link) => (
                <li key={link.href}>
                  <a href={link.href}>{link.label}</a>
                </li>
              ))}
            </ul>
          </nav>
        )}
        <a className="lc-btn" href={APP_STORE_URL} onClick={onCtaClick}>
          {content.nav.compactCta}
        </a>
      </div>
    </header>
  );
}

export function AppStoreBadge({ className = '' }) {
  const onClick = useStoreClick();
  return (
    <a
      className={`lc-badge ${className}`.trim()}
      href={APP_STORE_URL}
      onClick={onClick}
    >
      <AppStoreBadgeSvg />
    </a>
  );
}

/**
 * A responsive <img> from the generated image index. Renders nothing when the
 * source capture has not been supplied yet.
 *
 * Every shot loads eagerly. With loading="lazy" the below-the-fold captures
 * only started downloading as they scrolled near, which reads as pop-in
 * (Safari's lazy threshold is short). They are 40-75 KB WebPs, so fetching
 * them all on render is cheap.
 */
export function Shot({ name, alt, sizes, priority = false, className = '' }) {
  const image = images[name];
  if (!image) return null;
  const extra = priority
    ? { fetchpriority: 'high', decoding: 'sync' }
    : { decoding: 'async' };
  return (
    <img
      className={className}
      src={image.src}
      srcSet={image.srcSet}
      sizes={sizes}
      width={image.width}
      height={image.height}
      alt={alt}
      {...extra}
    />
  );
}

export function Phone({ name, alt, sizes, priority = false }) {
  if (!images[name]) return null;
  return (
    <div className="lc-phone">
      <Shot name={name} alt={alt} sizes={sizes} priority={priority} />
    </div>
  );
}

export function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="lc-footer">
      <div className="lc-container lc-footer-inner">
        <div className="lc-footer-links">
          <Link to={PRIVACY_PATH}>{content.footer.privacy}</Link>
          <a href={`mailto:${SUPPORT_EMAIL}`}>
            {content.footer.support}: {SUPPORT_EMAIL}
          </a>
        </div>
        <p className="lc-footer-copy">
          © {year} {content.copyright}
        </p>
      </div>
    </footer>
  );
}
